import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

export interface BatchOutcome {
  joined: number;
  optedOut: number;
}

/**
 * Per-batch joined/opted-out counts for the "Last 14 days" table (the invite
 * campaign row itself only carries sent/delivered/failed, not reply outcomes).
 * One paged read of the outcome column across the visible batches, aggregated
 * client-side into a { campaignId → {joined, optedOut} } map, so a single
 * query serves the whole table regardless of batch count.
 */
export const useInviteBatchOutcomes = (campaignIds: string[]) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<Record<string, BatchOutcome>>({
    queryKey: ["sms-invite-batch-outcomes", campaignIds],
    enabled: campaignIds.length > 0,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const pageSize = 1000;
      const map: Record<string, BatchOutcome> = {};
      for (const id of campaignIds) map[id] = { joined: 0, optedOut: 0 };

      for (let from = 0; ; from += pageSize) {
        const { data, error } = await sb
          .from("campaign_recipients")
          .select("campaign_id, outcome")
          .in("campaign_id", campaignIds)
          .in("outcome", ["subscribed", "opted_out"])
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const rows = (data ?? []) as { campaign_id: string; outcome: string }[];
        for (const r of rows) {
          const bucket = map[r.campaign_id] ?? (map[r.campaign_id] = { joined: 0, optedOut: 0 });
          if (r.outcome === "subscribed") bucket.joined += 1;
          else if (r.outcome === "opted_out") bucket.optedOut += 1;
        }
        if (rows.length < pageSize) break;
      }
      return map;
    },
    staleTime: 60 * 1000,
  });
};
